import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Ban, Copy, Download, FileText, Send, SendHorizontal, Trash2, Undo2, CheckCircle2, PackageCheck } from "lucide-react";
import { api } from "../../api/client";
import { Button, buttonClass } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { useToast } from "../../components/Toast";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { workOrderExportUrl } from "../../api/workorders";
import type { WorkOrder, WorkOrderHistoryEntry } from "../../api/workorders";
import { SendWorkOrderDialog } from "./SendWorkOrderDialog";

// 動作列(規格 §8):儲存在編輯器;這裡是送覆核、(老闆)覆核通過 / 退回、下載 PDF、下載 Word、寄出、標記完成、複製、作廢。
// 哪一顆出現由狀態決定;能不能覆核由後端算好(`can_review`),前端不自己比角色。

export interface WorkOrderActionsProps {
  workOrder: WorkOrder;
  /** 編輯器裡有沒存的修改。有的話不能送覆核 / 寄出 —— 送出去的會是舊的那一版。 */
  dirty: boolean;
  onChanged: (wo: WorkOrder) => void;
  onOpenWorkOrder: (id: number) => void;
  /** 刪除成功之後(工單已進回收桶):上層導回列表。 */
  onDeleted?: () => void;
  /** 標題列左側(工單號、種類、狀態)。 */
  title?: ReactNode;
  /** 標題列右側、動作鈕之前(未存檔提示與儲存鈕)。 */
  lead?: ReactNode;
}

export function WorkOrderActions({ workOrder, dirty, onChanged, onOpenWorkOrder, onDeleted, title, lead }: WorkOrderActionsProps) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  const [sending, setSending] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const inFlight = useRef(false);
  const { id, status } = workOrder;

  const run = async (path: string, body?: unknown, done?: string): Promise<WorkOrder | null> => {
    if (inFlight.current) return null;  // 上一個動作還沒回來:不再送第二次
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const got = await api.post<WorkOrder>(`/work-orders/${id}/${path}`, body);
      if (done) toast.push(done, "success");
      return got;
    } catch (e) {
      setError(e instanceof Error ? e.message : "動作失敗");
      return null;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const act = async (path: string, body?: unknown, done?: string): Promise<boolean> => {
    const got = await run(path, body, done);
    if (got) onChanged(got);
    return got !== null;
  };

  const duplicate = async () => {
    const got = await run("duplicate", undefined, "已複製成新工單");
    if (got) onOpenWorkOrder(got.id);
  };

  const remove = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      await api.del(`/work-orders/${id}`);
      toast.push("已刪除,7 天內可在回收桶還原", "success");
      setDeleting(false);
      onDeleted?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "刪除失敗");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const canSend = status === "APPROVED" || status === "SENT";
  const canDelete = status === "DRAFT" || status === "VOID";
  const canVoid = status !== "SENT" && status !== "COMPLETED" && status !== "VOID";
  const canComplete = status === "APPROVED" || status === "SENT";

  return (
    <section aria-label="工單動作" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {title}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {lead}
          {status === "DRAFT" ? (
            <Button type="button" size="sm" variant="primary" icon={<SendHorizontal aria-hidden="true" size={16} />} disabled={busy || dirty} onClick={() => void act("submit", undefined, "已送覆核")}>送覆核</Button>
          ) : null}
          {workOrder.can_review ? (
            <>
              <Button type="button" size="sm" variant="primary" icon={<CheckCircle2 aria-hidden="true" size={16} />} disabled={busy || dirty} onClick={() => void act("approve", undefined, "已覆核通過")}>覆核通過</Button>
              <Button type="button" size="sm" variant="secondary" icon={<Undo2 aria-hidden="true" size={16} />} disabled={busy || dirty} onClick={() => { setReason(""); setError(null); setReturning(true); }}>退回</Button>
            </>
          ) : null}
          <a href={workOrderExportUrl(id, "pdf")} className={buttonClass("secondary", "sm", "no-underline")}><Download aria-hidden="true" size={16} />下載 PDF</a>
          <a href={workOrderExportUrl(id, "docx")} className={buttonClass("secondary", "sm", "no-underline")}><FileText aria-hidden="true" size={16} />下載 Word</a>
          {canSend ? (
            <Button type="button" size="sm" variant="primary" icon={<Send aria-hidden="true" size={16} />} disabled={busy || dirty} onClick={() => setSending(true)}>
              {status === "SENT" ? "再寄一次" : "寄出"}
            </Button>
          ) : null}
          {canComplete ? (
            <Button type="button" size="sm" variant="secondary" icon={<PackageCheck aria-hidden="true" size={16} />} disabled={busy || dirty} onClick={() => setCompleting(true)}>標記完成</Button>
          ) : null}
          <Button type="button" size="sm" variant="secondary" icon={<Copy aria-hidden="true" size={16} />} disabled={busy} onClick={() => (dirty ? setDuplicating(true) : void duplicate())}>複製</Button>
          {canVoid ? <Button type="button" size="sm" variant="danger" icon={<Ban aria-hidden="true" size={16} />} disabled={busy} onClick={() => { setError(null); setVoiding(true); }}>作廢</Button> : null}
          {canDelete ? <Button type="button" size="sm" variant="danger" icon={<Trash2 aria-hidden="true" size={16} />} disabled={busy} onClick={() => { setError(null); setDeleting(true); }}>刪除</Button> : null}
        </div>
      </div>
      {dirty && (status === "DRAFT" || status === "APPROVED" || status === "SENT" || workOrder.can_review) ? (
        <p className="m-0 text-xs text-[var(--text-3)]">有沒存的修改,請先儲存。</p>
      ) : null}
      {dirty ? <p className="m-0 text-xs text-[var(--text-3)]">下載的是上次儲存的版本。</p> : null}
      {status === "DRAFT" && !workOrder.other_owner_available ? (
        <p role="note" className="m-0 text-sm text-[var(--warn)]">目前沒有人可以覆核工單,請老闆到設定的「覆核指派」加人。</p>
      ) : null}
      {status === "PENDING_REVIEW" && !workOrder.can_review ? <p className="m-0 text-sm text-[var(--text-2)]">等待覆核中。</p> : null}
      {error && !returning && !voiding && !deleting && !completing ? <p role="alert" className="m-0 text-sm text-[var(--danger)]">{error}</p> : null}

      <Dialog open={returning} onClose={() => setReturning(false)} title="退回工單"
        footer={<>
          <Button type="button" variant="secondary" onClick={() => setReturning(false)}>取消</Button>
          <Button type="button" variant="primary" disabled={busy || reason.trim() === ""}
            onClick={async () => { if (await act("return", { reason: reason.trim() }, "已退回")) setReturning(false); }}>退回</Button>
        </>}>
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium text-[var(--text-2)]">退回原因</span>
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
            className="rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 py-1.5 text-sm" />
        </label>
        {error ? <p role="alert" className="m-0 text-sm text-[var(--danger)]">{error}</p> : null}
      </Dialog>

      <Dialog open={voiding} onClose={() => setVoiding(false)} title="作廢這張工單?"
        footer={<>
          <Button type="button" variant="secondary" onClick={() => setVoiding(false)}>取消</Button>
          <Button type="button" variant="danger" disabled={busy}
            onClick={async () => { if (await act("void", undefined, "已作廢")) setVoiding(false); }}>作廢</Button>
        </>}>
        <p className="m-0 text-sm">作廢後不能再修改、不能寄出;要的話可以複製成新工單。</p>
        {dirty ? <p className="m-0 text-sm text-[var(--warn)]">有沒存的修改,確定要作廢嗎?沒存的修改會一起不見。</p> : null}
        {error ? <p role="alert" className="m-0 text-sm text-[var(--danger)]">{error}</p> : null}
      </Dialog>

      <Dialog open={completing} onClose={() => setCompleting(false)} title="標記這張工單已完成?"
        footer={<>
          <Button type="button" variant="secondary" onClick={() => setCompleting(false)}>取消</Button>
          <Button type="button" variant="primary" disabled={busy}
            onClick={async () => { if (await act("complete", undefined, "已標記完成")) setCompleting(false); }}>標記完成</Button>
        </>}>
        <p className="m-0 text-sm">標記完成後不能再修改、不能寄出、不能作廢;要的話可以複製成新工單。</p>
        {error ? <p role="alert" className="m-0 text-sm text-[var(--danger)]">{error}</p> : null}
      </Dialog>

      <Dialog open={deleting} onClose={() => setDeleting(false)} title="刪除這張工單?"
        footer={<>
          <Button type="button" variant="secondary" disabled={busy} onClick={() => setDeleting(false)}>取消</Button>
          <Button type="button" variant="danger" disabled={busy} onClick={() => void remove()}>刪除</Button>
        </>}>
        <p className="m-0 text-sm">{`要刪除工單 ${workOrder.number} 嗎?會放進回收桶,7 天內可以還原。`}</p>
        {error ? <p role="alert" className="m-0 text-sm text-[var(--danger)]">{error}</p> : null}
      </Dialog>

      <Dialog open={duplicating} onClose={() => setDuplicating(false)} title="複製這張工單?"
        footer={<>
          <Button type="button" variant="secondary" onClick={() => setDuplicating(false)}>取消</Button>
          <Button type="button" variant="primary" disabled={busy}
            onClick={async () => { await duplicate(); setDuplicating(false); }}>複製</Button>
        </>}>
        <p className="m-0 text-sm">有沒存的修改,確定要複製嗎?複製的是上次儲存的版本,沒存的修改不會帶過去。</p>
      </Dialog>

      <SendWorkOrderDialog open={sending} workOrderId={id} onClose={() => setSending(false)}
        onSent={(got) => { setSending(false); toast.push("已寄出", "success"); onChanged(got); }} />
    </section>
  );
}

export function WorkOrderHistory({ history }: { history: WorkOrderHistoryEntry[] }) {
  return (
    <section aria-label="狀態歷程" className="flex flex-col gap-2">
      <h3 className="m-0 text-base font-semibold text-[var(--text)]">狀態歷程</h3>
      <ol className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
        {history.map((entry, index) => (
          <li key={`${entry.action}-${index}`} className="flex flex-wrap gap-2">
            <span className="text-[var(--text-3)]">{formatTaipeiDateTime(entry.at)}</span>
            <span>{entry.user_name}</span>
            <span className="font-medium">{entry.label}</span>
            {entry.note ? <span className="text-[var(--text-2)]">{entry.note}</span> : null}
          </li>
        ))}
      </ol>
    </section>
  );
}
